import { IconBuildingBank, IconPlus } from "@tabler/icons-react";
import styles from "./BankList.module.css";
import EmptyState from "../UI/EmptyState";
import BankCard from "./BankCard";
import BankConnectionGroup from "./BankConnectionGroup";
// @ts-expect-error pure js
import useLinxo from "../../hooks/use-linxo";
// @ts-expect-error pure js
import { LinxoUsage } from "../../utils/constants/linxo";
// @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx";
// @ts-expect-error pure js
import Spinner from "../UI/Spinner";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
import { useState } from "react";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import Callout from "../UI/Callout";
import Button from "../UI/Button";
import Text from "../UI/Text";
// @ts-expect-error pure js
import useGroupBy from "../../hooks/use-group-by.js";
import { type Bank } from "../../models/bank";

interface BankListProps {
  banks: Bank[];
  isLoading?: boolean;
  onShowTransactions: (bank: Bank) => void;
}

export default function BankList({
  banks,
  isLoading,
  onShowTransactions,
}: BankListProps) {
  const { createWidgetSession } = useLinxo();
  const [RedirectModal, closeRedirectModal, openRedirectModal] = useModal();
  const [ConfirmModal, closeConfirmModal, openConfirmModal] = useModal();
  const [error, setError] = useState("");

  async function handleManageAccounts(
    connectionId: string,
    channelDefinitionId: string,
  ) {
    setError("");
    openRedirectModal();
    try {
      const url = await createWidgetSession(LinxoUsage.MANAGE_ACCOUNTS, {
        connection_id: connectionId,
        channel_definition_id: channelDefinitionId,
      });
      closeRedirectModal();
      window.location.assign(url);
    } catch (e) {
      setError(handleAPIError(e));
    }
  }

  async function handleLinxoReconnect(
    connectionId: string | null,
    channelDefinitionId: string | null,
  ) {
    setError("");
    openRedirectModal();
    try {
      let url;
      if (connectionId === null && channelDefinitionId === null) {
        url = await createWidgetSession(LinxoUsage.ADD_CONNECTION);
      } else {
        url = await createWidgetSession(LinxoUsage.EDIT_CREDENTIALS, {
          connection_id: connectionId,
          channel_definition_id: channelDefinitionId,
        });
      }
      closeRedirectModal();
      window.location.assign(url);
    } catch (e) {
      setError(handleAPIError(e));
    }
  }

  async function handleConfirmAddConnection() {
    closeConfirmModal();
    setError("");
    openRedirectModal();
    try {
      const url = await createWidgetSession(LinxoUsage.ADD_CONNECTION);
      closeRedirectModal();
      window.location.assign(url);
    } catch (e) {
      setError(handleAPIError(e));
    }
  }

  const { [null as unknown as string]: ungroupedBanks, ...groupedBanks } =
    useGroupBy(banks, {
      attr: "connection_id",
    }) as Record<string, Bank[]>;

  if (isLoading) return <IsLoading />;

  return (
    <>
      <RedirectModal>
        <Text className="marginB10">Redirection en cours...</Text>
        {!error && <Spinner width="3em" />}
        <Callout variant="danger">{error}</Callout>
      </RedirectModal>
      <ConfirmModal size="medium">
        <Text className="marginB10">
          <strong>Attention :</strong> Avant d'ajouter un nouveau compte
          bancaire, veuillez-vous assurer que vous n'avez pas déjà synchronisé
          un autre compte de la même banque, avec le même identifiant de
          connexion.
        </Text>
        <Text className="marginB20">
          Si c'est le cas, allez sur le compte déjà synchronisé et cliquez sur
          "Ajouter un compte supplémentaire".
        </Text>
        <div className="flex justify-content-end gap10">
          <Button variant="secondary" onClick={closeConfirmModal}>
            Annuler
          </Button>
          <Button onClick={handleConfirmAddConnection}>Continuer</Button>
        </div>
      </ConfirmModal>

      {banks.length === 0 ? (
        <EmptyState
          icon={IconBuildingBank}
          title="Aucun compte bancaire connecté"
          description="Connectez votre banque pour importer automatiquement vos opérations et rapprocher les paiements de vos locataires."
        >
          <Button icon={IconPlus} onClick={handleConfirmAddConnection}>
            Ajouter un compte bancaire
          </Button>
        </EmptyState>
      ) : (
        <div className={styles.container}>
          <div>
            <Button onClick={openConfirmModal}>
              <IconPlus size={16} />
              Ajouter un compte bancaire
            </Button>
          </div>

          {Object.entries(groupedBanks).map(([connectionId, groupBanks]) => (
            <BankConnectionGroup
              key={connectionId}
              connectionId={connectionId}
              banks={groupBanks}
              onLinxoReconnect={handleLinxoReconnect}
              onManageAccounts={handleManageAccounts}
              onShowTransactions={onShowTransactions}
            />
          ))}

          {ungroupedBanks?.length > 0 && (
            <div className={styles.cardsRow}>
              {ungroupedBanks.map((bank: Bank) => (
                <BankCard
                  key={bank.id}
                  bank={bank}
                  onLinxoReconnect={handleLinxoReconnect}
                  onManageAccounts={handleManageAccounts}
                  onShowTransactions={onShowTransactions}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
